import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  formatDecimal,
  formatReportingTableDate,
} from "../../ReportingSummary/reportingSummaryUtils";
import { formatCurrency } from "../../../utils/financialFormatters";

const pct = (n: number | undefined) => `${formatDecimal(n ?? 0, 2)}%`;

const formatIntegerCount = (n: number | undefined) =>
  Number(n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

export const CASINO_FINANCE_SORT_FIELD_MAP = {
  date: "date",
  players: "players",
  rounds: "rounds",
  turnover: "turnover",
  wins: "wins",
  rtpPct: "rtp",
  avgBet: "avg_bet",
  ggr: "ggr",
} as const;

export type CasinoFinanceSortKey = keyof typeof CASINO_FINANCE_SORT_FIELD_MAP;

export type CasinoFinanceRow = {
  key: string;
  date?: string;
  totalGames: number;
  activeGames: number;
  players: number;
  rounds: number;
  betsCount: number;
  turnover: number;
  wins: number;
  rtpPct: number;
  avgBet: number;
  ggr: number;
};

export function getCasinoFinanceColumns(
  currencySymbol?: string,
): ColumnsType<CasinoFinanceRow> {
  const money = (v: number | undefined) => formatCurrency(v ?? 0, currencySymbol, 2);
  return [
    {
      title: AppStrings.CASINO_FINANCE_COL_DATE,
      dataIndex: "date",
      key: "date",
      sorter: false,
      render: (value: string | undefined) => {
        const d = value ? dayjs(value) : null;
        const label =
          d?.isValid() ? formatReportingTableDate(d) : "—";
        return <span className="casino-finance-date-cell">{label}</span>;
      },
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_PLAYERS,
      dataIndex: "players",
      key: "players",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => formatIntegerCount(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_ROUNDS,
      dataIndex: "rounds",
      key: "rounds",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => formatIntegerCount(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_TURNOVER,
      dataIndex: "turnover",
      key: "turnover",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => money(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_WINS,
      dataIndex: "wins",
      key: "wins",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => money(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_RTP,
      dataIndex: "rtpPct",
      key: "rtpPct",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => pct(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_AVG_BET,
      dataIndex: "avgBet",
      key: "avgBet",
      sorter: true,
      align: "center",
      width: 200,
      render: (v: number | undefined) => money(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_GGR,
      dataIndex: "ggr",
      key: "ggr",
      sorter: true,
      align: "center",
      render: (v: number | undefined) => {
        const amount = v ?? 0;
        const colorClass =
          amount > 0
            ? "casino-finance-ggr-positive"
            : amount < 0
              ? "casino-finance-ggr-negative"
              : undefined;
        return <span className={colorClass}>{money(amount)}</span>;
      },
    },
  ];
}
